import React, { useState } from 'react';
import SharedLayout from '@/components/SharedLayout';
import MaterialGrid from '@/components/MaterialGrid';
import MaterialInputModal from '@/components/MaterialInputModal';
import MaterialList from '@/components/MaterialList';
import CutOptimizer from '@/components/CutOptimizer';
import SavedListsView from '@/components/SavedListsView';
import { MaterialCategory, MaterialItem } from '@/types';
import { Button } from '@/components/ui/button';
import { Trash2, List } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { SavedList } from '@/lib/indexedDBService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TutorialSlideShow from '@/components/TutorialSlideShow';

const MetalFab: React.FC = () => {
  const { toast } = useToast();
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<MaterialCategory | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('materials');
  const [showTutorial, setShowTutorial] = useState(false);

  const handleSelectMaterial = (category: MaterialCategory) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleAddItem = (item: MaterialItem) => {
    setMaterials([...materials, item]);
    setIsModalOpen(false);
    toast({
      title: "Material Added",
      description: `Added ${item.name} to your list.`,
    });
  };

  const handleEditItem = (updatedItem: MaterialItem) => {
    setMaterials(materials.map(item =>
      item.id === updatedItem.id ? updatedItem : item
    ));
    toast({
      title: "Material Updated",
      description: `Updated ${updatedItem.name} in your list.`,
    });
  };

  const handleDeleteItem = (itemId: string) => {
    setMaterials(materials.filter(item => item.id !== itemId));
    toast({
      title: "Material Removed",
      description: "Item removed from your list.",
    });
  };

  const handleClearAll = () => {
    setMaterials([]);
    toast({
      title: "List Cleared",
      description: "All materials have been removed from your list.",
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleLoadSavedList = (savedList: SavedList) => {
    setMaterials(savedList.materials);
    setActiveTab('materials');
    toast({
      title: "List Loaded",
      description: `Loaded "${savedList.name}" with ${savedList.materials.length} items.`,
    });
  };

  return (
    <SharedLayout showTutorial={() => setShowTutorial(true)}>
      {/* Tutorial Slideshow */}
      <TutorialSlideShow open={showTutorial} onOpenChange={setShowTutorial} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 mb-8 bg-secondary/50 backdrop-blur-sm">
          <TabsTrigger
            value="materials"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-500"
          >
            Material Selection
          </TabsTrigger>
          <TabsTrigger
            value="systems"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-500"
          >
            System Selection
          </TabsTrigger>
          <TabsTrigger
            value="saved"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-500"
          >
            <List className="h-4 w-4 mr-2" />
            Saved Lists
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="materials"
          className="transition-all duration-800 ease-in-out animate-fade-in motion-reduce:animate-none w-full"
          style={{ transform: 'translateZ(0)' }}
        >
          <div className="space-y-8">
            {/* Material Selection */}
            <section>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-foreground">
                  Material Selection
                </h2>
              </div>
              <MaterialGrid onSelectMaterial={handleSelectMaterial} />
            </section>

            {/* Material List */}
            <section>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-foreground">
                  Material List
                </h2>
                {materials.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleClearAll}
                    className="border-glass/20 hover:border-glass"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All
                  </Button>
                )}
              </div>
              <MaterialList
                items={materials}
                onEditItem={handleEditItem}
                onDeleteItem={handleDeleteItem}
              />
            </section>

            {/* Cut Optimization */}
            <section>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">
                  Cut Optimization
                </h2>
              </div>
              <CutOptimizer items={materials} />
            </section>
          </div>
        </TabsContent>

        <TabsContent
          value="systems"
          className="transition-all duration-800 ease-in-out animate-fade-in motion-reduce:animate-none w-full"
          style={{ transform: 'translateZ(0)' }}
        >
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                System Selection
              </h2>
            </div>
            <div className="text-muted-foreground text-center p-8">
              System selection content will be added here.
            </div>
          </section>
        </TabsContent>

        <TabsContent
          value="saved"
          className="transition-all duration-800 ease-in-out animate-fade-in motion-reduce:animate-none w-full"
          style={{ transform: 'translateZ(0)' }}
        >
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                Saved Lists
              </h2>
            </div>
            <SavedListsView onLoadList={handleLoadSavedList} />
          </section>
        </TabsContent>
      </Tabs>

      <footer className="mt-12 text-center text-sm text-muted-foreground">
        <p>&copy; 2025 ServicePane. All rights reserved.</p>
      </footer>

      {/* Input Modal */}
      <MaterialInputModal
        category={selectedCategory}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddItem={handleAddItem}
      />
    </SharedLayout>
  );
};

export default MetalFab;
